'use client';

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PaymentMethodMutationResponse } from '@sola/schema/dotcom/proxy-api/paymentMethods.ts';
import { PaymentMethodTypesEnum } from '@sola/schema/dotcom/supporters/paymentMethods.ts';
import type { SupporterPaymentMethodDetails } from '@sola/schema/dotcom/supporters/paymentMethods.ts';
import type { GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { encryptCreditCardData } from '@sola/shared-utils/credit-cards.ts';
import type { UserError } from '@sola/shared-utils/errors-user.ts';
import { userErrorPaymentMethodUpdate } from '@sola/shared-utils/errors/supporters.ts';
import { isUserError } from '@sola/shared-utils/httpClient.ts';
import { MUTATION_KEYS, QUERY_KEYS } from '@sola/supporter-sdk/constants.ts';
import { useReadyForQueries, useSupporterMutation } from '@sola/supporter-sdk/hooks/base.ts';
import { updateCommitmentCache } from '../commitments/hooks.ts';
import type { PaymentMethodMutationError } from './requests.ts';
import { addSupporterPaymentMethod, removeSupporterPaymentMethod, updateSupporterPaymentMethod } from './requests.ts';
export type CreatePaymentMethodMutationResult = {
  paymentMethod: GraphQLSupporterPaymentMethod;
  commitmentUpdates?: PaymentMethodMutationResponse['commitmentUpdates'];
  failedCommitmentUpdateCount: number;
  success?: boolean;
  isPartialSuccess: boolean;
};
const isUserErrorArray = (error: unknown): error is UserError[] => Array.isArray(error) && error.every(isUserError);

/**
 * Successful object to pass back to call site.
 */
const createPaymentMethodMutationResult = (response: PaymentMethodMutationResponse): CreatePaymentMethodMutationResult => {
  const failedCommitmentUpdateCount = response.commitmentUpdates?.failed.length || 0;
  return {
    paymentMethod: response.paymentMethod,
    commitmentUpdates: response.commitmentUpdates,
    failedCommitmentUpdateCount,
    success: response.success,
    isPartialSuccess: failedCommitmentUpdateCount > 0
  };
};
type PaymentMethodAddMutationVariables = {
  paymentMethod: SupporterPaymentMethodDetails;
  commitmentIds?: string[];
};

/**
 * Hook to add a new payment method.
 */
export const usePaymentMethodAdd = () => {
  const $ = _c(8);
  const queryClient = useQueryClient();
  const {
    globalId
  } = useReadyForQueries();
  let t0;
  if ($[0] !== globalId) {
    t0 = async t1 => {
      const {
        paymentMethod,
        commitmentIds
      } = t1;
      const paymentMethodDetails = {
        ...paymentMethod
      };
      if (paymentMethodDetails.type === PaymentMethodTypesEnum.Card) {
        const {
          cardNumber,
          cvv
        } = paymentMethodDetails;
        const {
          creditCardEncrypted,
          cvvEncrypted
        } = encryptCreditCardData({
          cvv,
          creditCard: cardNumber
        }, !!cvv, window.TxEncrypt, process.env.NEXT_PUBLIC_TOKENEX_ENCRYPTION_KEY as string);
        paymentMethodDetails.cardNumber = creditCardEncrypted ?? "";
        paymentMethodDetails.cvv = cvvEncrypted ?? "";
      }
      const response = await addSupporterPaymentMethod({
        globalId: globalId as string,
        paymentMethodDetails,
        commitmentIds
      });
      if (isUserError(response) || isUserErrorArray(response)) {
        throw response;
      }
      return createPaymentMethodMutationResult(response);
    };
    $[0] = globalId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== globalId || $[3] !== queryClient) {
    t1 = result => {
      updateCommitmentCache(queryClient, result.commitmentUpdates, globalId);
      const {
        paymentMethod: paymentMethod_0
      } = result;
      if (!paymentMethod_0.id) {
        return;
      }
      queryClient.setQueryData([QUERY_KEYS.supporterPaymentMethods, globalId], t2 => {
        const currentPaymentMethods = t2 === undefined ? [] : t2;
        return [...currentPaymentMethods, paymentMethod_0];
      });
    };
    $[2] = globalId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useSupporterMutation(t2);
};
type PaymentMethodUpdateMutationVariables = {
  paymentMethod: GraphQLSupporterPaymentMethod;
  commitmentIds?: string[];
};

/**
 * Hook to update a payment method.
 */
export const usePaymentMethodUpdate = () => {
  const $ = _c(8);
  const queryClient = useQueryClient();
  const {
    globalId
  } = useReadyForQueries();
  let t0;
  if ($[0] !== globalId) {
    t0 = async t1 => {
      const {
        paymentMethod,
        commitmentIds
      } = t1;
      const response = await updateSupporterPaymentMethod({
        globalId: globalId as string,
        paymentMethod,
        commitmentIds
      });
      if (isUserError(response) || isUserErrorArray(response)) {
        throw response;
      }
      if (!response?.paymentMethod) {
        throw userErrorPaymentMethodUpdate();
      }
      return createPaymentMethodMutationResult(response);
    };
    $[0] = globalId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== globalId || $[3] !== queryClient) {
    t1 = result => {
      updateCommitmentCache(queryClient, result.commitmentUpdates, globalId);
      const {
        paymentMethod: paymentMethod_0
      } = result;
      if (!paymentMethod_0.id) {
        return;
      }
      queryClient.setQueryData([QUERY_KEYS.supporterPaymentMethods, globalId], t2 => {
        const currentPaymentMethods = t2 === undefined ? [] : t2;
        const paymentMethodIndex = currentPaymentMethods.findIndex(existingPaymentMethod => existingPaymentMethod.id === paymentMethod_0.id);
        const updatedMethods = [...currentPaymentMethods];
        updatedMethods[paymentMethodIndex] = paymentMethod_0;
        return updatedMethods;
      });
    };
    $[2] = globalId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
};

/**
 * Hook to remove a payment method.
 */
export const usePaymentMethodRemove = paymentMethodIdKey => {
  const $ = _c(13);
  const queryClient = useQueryClient();
  const {
    globalId
  } = useReadyForQueries();
  let t0;
  if ($[0] !== paymentMethodIdKey) {
    t0 = [MUTATION_KEYS.paymentMethodRemove, paymentMethodIdKey];
    $[0] = paymentMethodIdKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== globalId || $[3] !== paymentMethodIdKey) {
    t1 = async newPaymentMethodId => {
      const response = await removeSupporterPaymentMethod(globalId, paymentMethodIdKey, newPaymentMethodId);
      return createPaymentMethodMutationResult(response);
    };
    $[2] = globalId;
    $[3] = paymentMethodIdKey;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== globalId || $[6] !== paymentMethodIdKey || $[7] !== queryClient) {
    t2 = (res, _newPaymentMethodId) => {
      const {
        success,
        commitmentUpdates
      } = res;
      if (success && !commitmentUpdates?.failed?.length) {
        queryClient.setQueryData([QUERY_KEYS.supporterPaymentMethods, globalId], paymentMethods => paymentMethods && paymentMethodIdKey ? paymentMethods?.filter(paymentMethod => paymentMethod?.id !== paymentMethodIdKey) : paymentMethods ?? []);
      }
      if (commitmentUpdates?.updated?.length) {
        updateCommitmentCache(queryClient, commitmentUpdates, globalId);
      }
    };
    $[5] = globalId;
    $[6] = paymentMethodIdKey;
    $[7] = queryClient;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t0 || $[10] !== t1 || $[11] !== t2) {
    t3 = {
      mutationKey: t0,
      mutationFn: t1,
      onSuccess: t2
    };
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return useSupporterMutation(t3);
};